import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress,
  Box,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import MDButton from 'components/MDButton';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import DataTable from 'examples/Tables/DataTable';
import { useServicePlanApi } from '../services/servicePlanApi';
// Using QA-domain APIs so feature IDs match what /features/attach expects.
// Shop APIs (api/categoriesApi, api/featuresApi) use different ID spaces → "feature not found".
import { Notify } from 'notiflix';

const columns = [
  { Header: '#', accessor: 'sno', width: '5%', align: 'left' },
  { Header: 'Answer Text', accessor: 'answerText', align: 'left' },
  { Header: 'Active', accessor: 'active', align: 'center' },
  { Header: 'Actions', accessor: 'actions', align: 'center' },
];

function AnswerOptionsModal({ open, onClose, question }) {
  const { answerOptionsApi } = useServicePlanApi();

  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingAnswer, setEditingAnswer] = useState(null);
  const [formData, setFormData] = useState({
    answerText: '',
    isActive: true,
    meta: '',
  });

  useEffect(() => {
    if (open && question) {
      fetchAnswers();
    }
  }, [open, question]);

  const fetchAnswers = async () => {
    setLoading(true);
    try {
      const response = await answerOptionsApi.getByQuestion(question.id);
      setAnswers(response.data.data || response.data);
    } catch {
      Notify.failure('Failed to fetch answer options');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveAnswer = async () => {
    if (!formData.answerText.trim()) {
      Notify.failure('Answer text is required');
      return;
    }

    let payload;
    try {
      payload = {
        ...formData,
        questionId: question.id,
        orderIndex: Number(formData.orderIndex) || 0,
        meta: formData.meta ? JSON.parse(formData.meta) : null,
      };
    } catch {
      Notify.failure('Meta must be valid JSON');
      return;
    }

    setLoading(true);
    try {
      if (editingAnswer) {
        await answerOptionsApi.update(editingAnswer.id, payload);
        Notify.success('Answer updated successfully');
      } else {
        await answerOptionsApi.create(payload);
        Notify.success('Answer created successfully');
      }

      resetForm();
      fetchAnswers();
    } catch (error) {
      Notify.failure(error.response?.data?.message || 'Failed to save answer');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (answer) => {
    setEditingAnswer(answer);
    setFormData({
      answerText: answer.answerText,
      isActive: answer.isActive,
      meta: answer.meta ? JSON.stringify(answer.meta) : '',
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this answer option?')) return;
    try {
      const answer = answers.find((a) => a.id === id);
      await answerOptionsApi.delete(id, answer?.answerText || '');
      Notify.success('Answer deleted successfully');
      fetchAnswers();
    } catch {
      Notify.failure('Failed to delete answer');
    }
  };

  const resetForm = () => {
    setEditingAnswer(null);
    setFormData({ answerText: '', isActive: true, meta: '' });
  };

  const getRow = (answer, idx) => ({
    sno: <MDTypography variant="caption" fontWeight="medium">{idx + 1}</MDTypography>,
    answerText: <MDTypography variant="caption">{answer.answerText}</MDTypography>,
    active: (
      <MDBox ml={-1}>
        <MDBadge
          badgeContent={answer.isActive ? 'Yes' : 'No'}
          color={answer.isActive ? 'success' : 'secondary'}
          variant="gradient"
          size="sm"
        />
      </MDBox>
    ),
    actions: (
      <MDBox>
        <IconButton size="small" color="primary" onClick={() => handleEdit(answer)}>
          <Edit />
        </IconButton>
        <IconButton size="small" color="error" onClick={() => handleDelete(answer.id)}>
          <Delete />
        </IconButton>
      </MDBox>
    ),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Manage Answer Options for: {question?.text}</DialogTitle>
      <DialogContent>
        <MDBox pt={2}>
          {/* Add/Edit Form */}
          <Box mb={3} p={2} border="1px solid #e0e0e0" borderRadius={2}>
            <MDTypography variant="h6" mb={2}>
              {editingAnswer ? 'Edit Answer' : 'Add New Answer'}
            </MDTypography>

            <TextField
              fullWidth
              label="Answer Text"
              name="answerText"
              value={formData.answerText}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Meta (JSON, optional)"
              name="meta"
              value={formData.meta}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={<Switch name="isActive" checked={formData.isActive} onChange={handleChange} />}
              label="Active"
              sx={{ mb: 2 }}
            />

            <MDBox display="flex" gap={1} mt={1}>
              <MDButton onClick={handleSaveAnswer} color="info" disabled={loading}>
                {loading ? <CircularProgress size={20} /> : editingAnswer ? 'Update' : 'Add'}
              </MDButton>
              {editingAnswer && (
                <MDButton onClick={resetForm} color="secondary">
                  Cancel
                </MDButton>
              )}
            </MDBox>
          </Box>

          {/* Answers List */}
          <MDTypography variant="h6" mb={2}>Existing Answers</MDTypography>
          {answers.length === 0 ? (
            <MDBox py={4} textAlign="center">
              <MDTypography variant="body2" color="text.secondary">
                No answer options found
              </MDTypography>
            </MDBox>
          ) : (
            <DataTable
              table={{ columns, rows: answers.map((answer, idx) => getRow(answer, idx)) }}
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={false}
              noEndBorder
              canSearch={false}
            />
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary">Close</MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default AnswerOptionsModal;
